import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import type { Customization, Order, OrderLine } from '@nucleus-commerce/api-client/admin';
import { isApiError } from '@nucleus-commerce/api-client/transport';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Label } from '@nucleus-commerce/ui/components/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';
import { Switch } from '@nucleus-commerce/ui/components/switch';

import { useCustomizations, useSizeSets } from '@/features/dictionary';

import { useUpdateOrderLine } from '../hooks/use-order-mutations';

type Answer = string | number | boolean;

export interface EditLineDialogProps {
  storeId: string;
  order: Order;
  line: OrderLine;
  onOpenChange: (open: boolean) => void;
}

/**
 * Corrects the answers of an unshipped line (a ring size typed wrong at checkout). The backend
 * validates them like a cart line and refuses with 409 ORDER_LINE_PRICE_WOULD_CHANGE when the
 * new answers would charge a different amount, since totals never change after placement.
 * File answers are shown but kept as they are.
 */
export function EditLineDialog({ storeId, order, line, onOpenChange }: EditLineDialogProps) {
  const { t } = useTranslation(['orders', 'common']);
  const message = useErrorMessage();
  const update = useUpdateOrderLine(storeId);
  const dictionary = useCustomizations();
  const sizeSets = useSizeSets();
  const [answers, setAnswers] = useState<Record<string, Answer>>(() =>
    Object.fromEntries(line.customizations.map((c) => [c.key, c.value])),
  );
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [rootError, setRootError] = useState<string | null>(null);

  const defs = useMemo(() => {
    const byKey = new Map((dictionary.data ?? []).map((d) => [d.key, d]));
    return line.customizations.map((c) => ({ current: c, def: byKey.get(c.key) ?? null }));
  }, [dictionary.data, line.customizations]);

  const optionsOf = (def: Customization) => {
    if (def.sizeSetId) {
      const set = (sizeSets.data ?? []).find((s) => s.id === def.sizeSetId);
      return set?.values.map((v) => ({ value: v.value, label: v.label })) ?? [];
    }
    return def.options ?? [];
  };

  const submit = async (event: React.SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    setRootError(null);
    setFieldErrors({});
    try {
      await update.mutateAsync({
        id: order.id,
        lineId: line.id,
        body: {
          customizations: line.customizations.map((c) => ({
            key: c.key,
            value: c.file ? c.value : (answers[c.key] ?? c.value),
          })),
        },
      });
      onOpenChange(false);
    } catch (error) {
      if (isApiError(error) && error.code === 'ORDER_LINE_PRICE_WOULD_CHANGE') {
        setRootError(t('orders:editLine.priceWouldChange'));
        return;
      }
      if (isApiError(error) && error.kind === 'conflict' && error.code === 'CONFLICT') {
        setRootError(t('orders:editLine.conflict'));
        return;
      }
      if (isApiError(error)) {
        const placed: Record<string, string> = {};
        for (const detail of error.details) {
          const index = /^body\.customizations\.?\[?(\d+)\]?/.exec(detail.field)?.[1];
          const key = index === undefined ? undefined : line.customizations[Number(index)]?.key;
          if (key) placed[key] = detail.message;
        }
        if (Object.keys(placed).length > 0) {
          setFieldErrors(placed);
          return;
        }
      }
      setRootError(message(error));
    }
  };

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('orders:editLine.title', { title: line.title })}</DialogTitle>
          <DialogDescription>{t('orders:editLine.description')}</DialogDescription>
        </DialogHeader>
        <form onSubmit={(event) => void submit(event)} noValidate className="flex flex-col gap-4">
          {rootError ? (
            <Alert variant="destructive" role="alert">
              <AlertDescription>{rootError}</AlertDescription>
            </Alert>
          ) : null}
          {defs.map(({ current, def }) => {
            const id = `line-${line.id}-${current.key}`;
            const error = fieldErrors[current.key];
            const value = answers[current.key] ?? current.value;
            const set = (next: Answer) => {
              setAnswers((all) => ({ ...all, [current.key]: next }));
              setFieldErrors(({ [current.key]: _gone, ...rest }) => rest);
            };
            return (
              <div key={current.key} className="flex flex-col gap-1.5">
                <Label htmlFor={id}>{current.label}</Label>
                {current.file ? (
                  <p className="text-muted-foreground text-sm">
                    {t('orders:editLine.fileKept', { name: current.file.filename })}
                  </p>
                ) : def?.inputType === 'select' ? (
                  <Select value={String(value)} onValueChange={set}>
                    <SelectTrigger id={id} className="w-full" aria-invalid={Boolean(error)}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {optionsOf(def).map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : def?.inputType === 'boolean' ? (
                  <Switch id={id} checked={value === true} onCheckedChange={set} />
                ) : (
                  <Input
                    id={id}
                    value={String(value)}
                    inputMode={def?.inputType === 'number' ? 'decimal' : undefined}
                    maxLength={def?.maxLength ?? undefined}
                    aria-invalid={Boolean(error)}
                    onChange={(e) =>
                      set(def?.inputType === 'number' ? Number(e.target.value) : e.target.value)
                    }
                  />
                )}
                {error ? (
                  <p role="alert" className="text-destructive text-sm">
                    {error}
                  </p>
                ) : null}
              </div>
            );
          })}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              {t('common:actions.cancel')}
            </Button>
            <Button type="submit" disabled={update.isPending}>
              {update.isPending ? t('common:actions.saving') : t('common:actions.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
